import { ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

const REQUEST_BODY_EXAMPLE = JSON.stringify(
  {
    query:
      "mutation NotifyImportCompleted($input: NotifyImportCompletedInput!) { notifyImportCompleted(input: $input) { success } }",
    variables: {
      input: {
        id: "evt-{uuid}",
        type: "importData.completed",
        createdAt: 1711234567,
        data: {
          importId: "{importId}",
          status: "ALLCOMPLETED | RECORDCOMPLETED | CHANGEREQUESTCOMPLETED | FAILED",
          message: "string | null",
        },
      },
    },
  },
  null,
  2,
);

const SUCCESS_RESPONSE_EXAMPLE = JSON.stringify({ data: { notifyImportCompleted: { success: true } } }, null, 2);

const ERROR_RESPONSE_EXAMPLE = JSON.stringify({ errors: [{ message: "error reason" }] }, null, 2);

// API constants — not translatable
const STATUS_VALUES = "ALLCOMPLETED | RECORDCOMPLETED | CHANGEREQUESTCOMPLETED | FAILED";

function CodeBlock({ label, content }: { label: string; content: string }) {
  return (
    <div className="space-y-1.5">
      <div className="text-12-medium capitalize text-fg-2">{label}</div>
      <pre className="overflow-x-auto rounded-[var(--radius-xs)] bg-bg-subtle p-3 font-mono text-12-regular text-fg-1">
        {content}
      </pre>
    </div>
  );
}

export function WebhookDocSection() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const rows: { label: string; value: ReactNode }[] = [
    { label: t("model.product.webhookDoc.methodLabel"), value: t("model.product.webhookDoc.method") },
    { label: t("model.product.webhookDoc.timeoutLabel"), value: t("model.product.webhookDoc.timeout") },
    { label: t("model.product.webhookDoc.retryLabel"), value: t("model.product.webhookDoc.retry") },
    {
      label: t("model.product.webhookDoc.retryConditionLabel"),
      value: t("model.product.webhookDoc.retryCondition"),
    },
    {
      label: t("model.product.webhookDoc.statusValuesLabel"),
      value: <code className="font-mono text-12-regular">{STATUS_VALUES}</code>,
    },
  ];

  return (
    <section className="overflow-hidden rounded-[var(--radius-md)] border border-border-default bg-white">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-16-bold capitalize text-fg-1 hover:bg-bg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-fg-3" /> : <ChevronRight className="h-4 w-4 text-fg-3" />}
        <span>{t("model.product.webhookDoc.title")}</span>
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-border-default p-3.5">
          <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
            <table className="w-full border-collapse">
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
                    <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left align-top text-12-medium text-fg-2">
                      {row.label}
                    </th>
                    <td className="px-3.5 py-2 text-14-regular text-fg-1">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <CodeBlock label={t("model.product.webhookDoc.requestBodyLabel")} content={REQUEST_BODY_EXAMPLE} />
          <CodeBlock label={t("model.product.webhookDoc.expectedResponseLabel")} content={SUCCESS_RESPONSE_EXAMPLE} />
          <CodeBlock label={t("model.product.webhookDoc.errorResponseLabel")} content={ERROR_RESPONSE_EXAMPLE} />
        </div>
      )}
    </section>
  );
}
